import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Every namespace file under locales/en|it/*.json is auto-registered — add a
// new namespace by dropping in a same-named file on both sides, no edits
// needed here. (CRA/webpack's require.context, not a runtime fs read.)
function loadLocale(ctx) {
  const out = {};
  ctx.keys().forEach((key) => {
    const ns = key.replace('./', '').replace('.json', '');
    out[ns] = ctx(key);
  });
  return out;
}

const en = loadLocale(require.context('./locales/en', false, /\.json$/));
const it = loadLocale(require.context('./locales/it', false, /\.json$/));
const es = loadLocale(require.context('./locales/es', false, /\.json$/));
const fr = loadLocale(require.context('./locales/fr', false, /\.json$/));
const de = loadLocale(require.context('./locales/de', false, /\.json$/));

// 2026-09-16: + spagnolo. Il backend ha la stessa lista in routes/profile.js.
export const SUPPORTED_LANGUAGES = ['en', 'it', 'es', 'fr', 'de']; // 2026-09-17: + francese; 2026-09-18: + tedesco
export const LANGUAGE_STORAGE_KEY = 'lc_language';

// Initial language, before any server profile is loaded: an explicit local
// choice wins (set by the switcher, or by a previous session), otherwise the
// browser's own language (navigator.language) with English fallback for
// anything that isn't Italian. No IP/geo signal here on purpose — see
// project memory: language is a browser/profile preference, not a redirect
// keyed off where the request came from.
function detectInitialLanguage() {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch (e) {}
  const nav = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
  if (nav.startsWith('it')) return 'it';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('de')) return 'de';
  return 'en';
}

i18n.use(initReactI18next).init({
  resources: { en, it, es, fr, de },
  lng: detectInitialLanguage(),
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: Object.keys(en),
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  // CHIAVI MANCANTI VISIBILI (2026-09-16). Prima un buco cadeva in silenzio
  // sull'inglese. Ora ogni chiave assente nella lingua corrente finisce in
  // console e, una volta per chiave e per sessione, al server
  // (POST /api/i18n/manca → log di pm2), cosi' un buco in es o in it si vede.
  saveMissing: true,
  saveMissingTo: 'current', // riporta la lingua che manca (es), non quella di ripiego (en)
  missingKeyHandler: (lngs, ns, key) => segnalaChiaveMancante(lngs, ns, key),
});

// Persists both to localStorage (so it survives a refresh before login/for
// logged-out visitors) and flips the live UI. Does NOT call the server —
// see hooks/useLanguage.js for the version that also saves it to the
// logged-in user's profile.
export function setLanguage(lang) {
  if (!SUPPORTED_LANGUAGES.includes(lang)) return;
  try { localStorage.setItem(LANGUAGE_STORAGE_KEY, lang); } catch (e) {}
  i18n.changeLanguage(lang);
}

i18n.on('languageChanged', (lang) => {
  document.documentElement.lang = lang;
});
document.documentElement.lang = i18n.language;

const segnalate = new Set();
function segnalaChiaveMancante(lngs, ns, key) {
  const lng = Array.isArray(lngs) ? lngs[0] : lngs;
  const firma = `${lng}|${ns}|${key}`;
  if (segnalate.has(firma)) return;
  segnalate.add(firma);
  // eslint-disable-next-line no-console
  console.warn(`[i18n] chiave mancante: ${ns}:${key} (${lng})`);
  try {
    const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
    const corpo = new Blob([JSON.stringify({ lng, ns, key })], { type: 'application/json' });
    if (navigator.sendBeacon) navigator.sendBeacon(`${base}/i18n/manca`, corpo);
  } catch (e) {}
}

// Gancio di collaudo (2026-09-16): window.__lcI18n.t('ns:chiave_inesistente')
// deve produrre l'avviso in console e il beacon al server. Serve a provare
// l'allarme delle chiavi mancanti di proposito, invece di fidarsi che scatti.
if (typeof window !== 'undefined') window.__lcI18n = i18n;

export default i18n;
